import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import classNames from 'classnames';
import s from './CompactSelect.module.css';

const getNormalizedValue = (value) => (
    value === null || value === undefined ? '' : String(value)
);

const CompactSelect = ({
    value,
    options = [],
    placeholder = 'Выберите',
    onChange,
    className = '',
    disabled = false,
    searchable = false,
    searchPlaceholder = 'Поиск',
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [menuPosition, setMenuPosition] = useState(null);
    const [query, setQuery] = useState('');
    const wrapperRef = useRef(null);
    const menuRef = useRef(null);
    const searchRef = useRef(null);

    const selectedOption = useMemo(
        () => options.find((option) => getNormalizedValue(option?.value) === getNormalizedValue(value)),
        [options, value],
    );

    useEffect(() => {
        const handleDocumentMouseDown = (event) => {
            if (
                !wrapperRef.current ||
                wrapperRef.current.contains(event.target) ||
                menuRef.current?.contains(event.target)
            ) return;

            setIsOpen(false);
        };

        document.addEventListener('mousedown', handleDocumentMouseDown);

        return () => {
            document.removeEventListener('mousedown', handleDocumentMouseDown);
        };
    }, []);

    useEffect(() => {
        if (!isOpen || !wrapperRef.current) return;

        const rect = wrapperRef.current.getBoundingClientRect();

        setMenuPosition({
            top: rect.bottom + 4,
            left: rect.left,
            minWidth: rect.width,
        });
    }, [isOpen]);

    // Поиск живёт только пока открыт список: закрыли — снова виден весь набор.
    useEffect(() => {
        if (!isOpen) {
            setQuery('');
            return;
        }

        searchRef.current?.focus();
    }, [isOpen]);

    const visibleOptions = useMemo(() => {
        const needle = query.trim().toLowerCase();

        if (!searchable || !needle) return options;

        return options.filter((option) => String(option?.label ?? '').toLowerCase().includes(needle));
    }, [options, query, searchable]);

    const handleSelect = (option) => {
        onChange?.(option.value);
        setIsOpen(false);
    };

    return (
        <div
            ref={wrapperRef}
            className={classNames(s.wrapper, className, { [s.disabled]: disabled })}
            onClick={(event) => event.stopPropagation()}
            onMouseDown={(event) => event.stopPropagation()}
        >
            <button
                type="button"
                className={s.button}
                disabled={disabled}
                title={String(selectedOption?.label ?? placeholder)}
                onClick={() => setIsOpen((prev) => !prev)}
            >
                <span className={s.value}>{selectedOption?.label ?? placeholder}</span>
                <span className={classNames(s.arrow, { [s.arrowOpen]: isOpen })} />
            </button>

            {isOpen && menuPosition && createPortal(
                <div
                    ref={menuRef}
                    className={classNames('new-design-scroll', s.menu)}
                    style={menuPosition}
                >
                    {searchable && (
                        <input
                            ref={searchRef}
                            type="text"
                            className={s.search}
                            placeholder={searchPlaceholder}
                            value={query}
                            onChange={(event) => setQuery(event.target.value)}
                        />
                    )}
                    {visibleOptions.length > 0 ? visibleOptions.map((option) => (
                        <button
                            key={getNormalizedValue(option.value)}
                            type="button"
                            className={classNames(s.option, {
                                [s.optionSelected]: getNormalizedValue(option.value) === getNormalizedValue(value),
                            })}
                            title={String(option.label)}
                            onClick={() => handleSelect(option)}
                        >
                            {option.label}
                        </button>
                    )) : (
                        <div className={s.empty}>{query.trim() ? 'Ничего не найдено' : 'Нет вариантов'}</div>
                    )}
                </div>,
                document.body,
            )}
        </div>
    );
};

export default CompactSelect;
